/**
 * CLAIM LAB — CLAUDECODE101 chapter 16, sorting a claim into one of four kinds.
 *
 * FOUNDER, 2026-08-15: "i want claude code chapters to deep dive".
 *
 * ── THIS CHAPTER TEACHES A CLASSIFICATION, WHICH IS PRACTISABLE ───────────────────────────────
 * Most of the ladder teaches judgement and an instrument can only illustrate it. §3 teaches a sort:
 * computed, quoted, admitted, or none of the three. A sort can be handed to a learner.
 *
 * ── THE CLAIMS ARE REAL, AND FOUR ARE FABRICATIONS THIS PROJECT SHIPPED ───────────────────────
 * Invented examples would teach sorting invented claims, which is easier — a made-up bad example is
 * written to look bad. Every claim here was actually made while building this academy, and three of
 * the four ungrounded ones went out before a mechanical check caught them.
 *
 * ── NO SCORE, AND THAT IS NOT SQUEAMISHNESS ──────────────────────────────────────────────────
 * A percentage would make this an examination, and the skill is not "get eight of ten". It is
 * noticing the fourth kind at all. So the lab shows what was sorted correctly, what was not, and
 * WHY in each case — and a learner who gets one wrong learns more from the reason than a mark could
 * carry. The same law the project spine and the capstone already hold.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  CLAIMS, KINDS, sortOf, isRight, shipped,
  NOT_THE_SAME_AS_FALSE, WHY_REAL_CLAIMS, THE_HABIT, SOURCE,
} from './claimsort.mjs';

export default function ClaimLab() {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});

  const sorted = Object.keys(revealed).length;
  const right = Object.entries(revealed).filter(([id]) => isRight(id, answers[id])).length;
  const missedFourth = CLAIMS.filter((c) => c.kind === 'ungrounded' && revealed[c.id] && answers[c.id] !== 'ungrounded');

  const pick = (id, kind) => {
    setAnswers((a) => ({ ...a, [id]: kind }));
    setRevealed((r) => ({ ...r, [id]: true }));
  };
  const reset = () => { setAnswers({}); setRevealed({}); };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Claim lab · which kind is this?</div>
      <h2 className="g-h2 lab-h">Ten real claims. Four of them are the kind to fear.</h2>
      <p className="g-body">
        <Value of={cited('Every claim in a technical answer is one of three things… Anything that is none of those three is a fourth kind, and it is the one to fear.', SOURCE)} />
      </p>
      <p className="g-body g-dim">{WHY_REAL_CLAIMS}</p>

      {/* ── THE FOUR KINDS, ALWAYS ON SCREEN ── */}
      <div className="clm-kinds">
        {KINDS.map((k) => (
          <div key={k.id} className={`clm-kind clm-${k.id}`}>
            <b>{k.name}</b>
            <p className="g-body g-dim">{k.says}</p>
          </div>
        ))}
      </div>

      {/* ── THE CLAIMS ── */}
      <div className="lab-task">
        <div className="g-label">Sort each one. Nothing is scored.</div>
        {CLAIMS.map((c) => {
          const done = revealed[c.id];
          const ok = done && isRight(c.id, answers[c.id]);
          return (
            <div key={c.id} className={`clm-claim ${done ? (ok ? 'clm-ok' : 'clm-miss') : ''}`}>
              <p className="g-body clm-text">“{c.text}”</p>
              {!done ? (
                <div className="lab-task-head">
                  {KINDS.map((k) => (
                    <button key={k.id} className="g-btn" onClick={() => pick(c.id, k.id)}>{k.name}</button>
                  ))}
                </div>
              ) : (
                <>
                  <p className="g-body clm-verdict">
                    {ok
                      ? <Value of={computed(`${KINDS.find((k) => k.id === c.kind).name} — yes`)} />
                      : <Value of={refused(`you said ${KINDS.find((k) => k.id === answers[c.id]).name}; it is ${KINDS.find((k) => k.id === c.kind).name}`)} />}
                  </p>
                  <p className="g-body">{c.why}</p>
                  <p className="g-body g-dim"><b>How you would check it:</b> {c.check}</p>
                  {c.caught_by && (
                    <p className="g-body clm-caught">
                      <b>This one shipped.</b> Caught by: {c.caught_by}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── WHAT THE SORTING SHOWED, WITHOUT A MARK ── */}
      {sorted > 0 && (
        <div className="lab-task">
          <div className="g-label">What that showed</div>
          <div className="lab-readout">
            <div><span className="g-label">Sorted</span><Value of={computed(`${sorted} of ${CLAIMS.length}`)} /></div>
            <div><span className="g-label">Matched</span><Value of={computed(String(right))} /></div>
            <div><span className="g-label">Fourth kind missed</span><Value of={computed(String(missedFourth.length))} /></div>
          </div>
          {missedFourth.length > 0 ? (
            <p className="g-body clm-punch">
              {missedFourth.length === 1 ? 'One of the ungrounded claims' : `${missedFourth.length} of the ungrounded claims`} read
              as something better than it was. That is the whole difficulty — a fourth-kind claim is
              generated <i>because</i> it is plausible, and plausibility is what makes it hard to catch.
              {shipped().length > 0 && ` Three of them shipped from this project before a check found them.`}
            </p>
          ) : sorted === CLAIMS.length ? (
            <p className="g-body clm-punch">
              Every fourth-kind claim spotted. Worth noticing what that took: in each case you asked
              which source would settle it, and found there was none. That question is the skill —
              not suspicion, which is exhausting and gets abandoned within a week.
            </p>
          ) : null}
          <p className="g-body g-dim">{THE_HABIT}</p>
          <button className="g-btn" onClick={reset}>Start again</button>
        </div>
      )}

      <p className="g-body clm-note"><b>Ungrounded is not the same as false.</b> {NOT_THE_SAME_AS_FALSE}</p>

      <p className="g-body g-dim lab-why">
        Nothing here is scored, because the skill is not getting eight of ten — it is noticing the
        fourth kind at all, and a learner who mis-sorts one learns more from the reason than a mark
        could carry.
        <br />
        Every claim is one this project actually made. The ungrounded ones are not illustrations
        written to look wrong: three of them shipped, in a course about not shipping them, and were
        caught by something mechanical rather than by anybody being careful.
      </p>
    </div>
  );
}
